import { useFavoriteTab } from '../../hooks/useFavoriteTab';
/**
 * USER HISTORY — what a set of people did, over a date range.
 *
 * The breadcrumb "My History" modal answers "what did I just do" for one person over
 * a trailing few days. This is the supervisory version: pick several people, pick a
 * range, read their work.
 *
 * TWO TABS, NOT ONE MERGED STREAM. The sources are different kinds of evidence and
 * interleaving them would invent a timeline nobody can audit:
 *
 *   RECORD CHANGES  cms.history — the durable audit trail. Field-level: table, key,
 *                   column, old value, new value. Years of retention. This is what
 *                   answers "who changed this order, and to what".
 *
 *   ACTIVITY        user_activity — frontend telemetry. Clicks, page views, searches,
 *                   PRO opens. It records that somebody LOOKED, which history cannot,
 *                   but it is sampled from the browser and proves nothing about the
 *                   database. A missing event means the tab was closed mid-batch just
 *                   as often as it means the thing never happened.
 *
 * ACTIVITY ONLY GOES BACK AS FAR AS RETENTION, and the page says so on its face rather
 * than letting a pruned window read as an idle employee. The Activity tab compares the
 * range you asked for against the oldest row actually retained for the people you
 * picked, and labels the gap. Record changes have no such limit.
 *
 * ONE RANGE AND ONE USER SET DRIVE BOTH TABS, so switching tabs re-asks the same
 * question of the other source instead of silently changing the question.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import { ProTableCell } from '../../components/ProTableCell';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import FilterBar from '../../components/FilterBar';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import HitsTrendChart from './userHistory/HitsTrendChart';
import RankedUsersChart from './userHistory/RankedUsersChart';
import userHistoryReportService, {
  ActivityReportRow,
  HistoryReportRow,
  ReportGroup,
  ReportUser,
  SeriesBucket,
  SeriesPoint,
} from '../../services/userHistoryReport.service';

type TabId = 'history' | 'activity';
type ViewId = 'ranked' | 'trend' | 'table';

const DEFAULT_DAYS = 30;

/** Past this many days the page opens on a summary rather than rows. A month of
 *  record changes is hundreds of thousands of rows and nobody reads that as a table;
 *  under a week the rows are few enough to just read. */
const CHART_DEFAULT_OVER_DAYS = 7;

/** Hourly only over short windows. 30 days hourly is 720 points per person, which
 *  draws but does not read; the selector still allows it deliberately. */
const HOURLY_MAX_DAYS = 7;

/** How far back the report can look. cms.history holds years, but an unbounded start
 *  turns every query into a scan of a 314M-row table — and the picker is built from
 *  the same window, so offering earlier dates would only ever return nothing. The
 *  server floors the start too; this just stops the calendar offering it. */
const MAX_LOOKBACK_DAYS = 90;

/** Row count past which the page says the result will be slow. Not a cap — the server
 *  applies none, and every matching row is returned. cms.history runs to thousands of
 *  rows per person per day, so a wide selection legitimately reaches six figures. */
const LARGE_RESULT_ROWS = 50000;

/** Local calendar date as YYYY-MM-DD. NOT toISOString(): that converts to UTC first,
 *  so a date picked west of UTC comes out as the previous day and quietly shifts the
 *  window by a day. */
const ymd = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Computed at mount, not as a module constant: a tab left open across midnight
 *  would otherwise keep filtering to the day it was opened on. */
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const daysAgo = (n: number) => {
  const d = startOfToday();
  d.setDate(d.getDate() - n);
  return d;
};

const formatDateTime = (value: string): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
};

const formatDuration = (ms: number | null): string => {
  if (ms == null) return '';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

const TruncatedCell: React.FC<{ value: string }> = ({ value }) => (
  <span className="block max-w-[16rem] truncate text-sm" title={value}>{value}</span>
);

const num = (v: number | null | undefined) => Number(v ?? 0).toLocaleString();

function UserHistoryReportContent() {
  const [users, setUsers] = useState<ReportUser[]>([]);
  const [groups, setGroups] = useState<ReportGroup[]>([]);
  /** Who the server actually resolved for the current selection. Drives the Ranked
   *  zero-rows, because a group's members are only known after expansion — and some
   *  of them are `v1:` people who are not in the `users` list at all. */
  const [resolvedUsers, setResolvedUsers] = useState<ReportUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | null>(() => daysAgo(DEFAULT_DAYS));
  const [dateTo, setDateTo] = useState<Date | null>(() => startOfToday());
  const [activeTab, setActiveTab] = useState<TabId>('history');
  useFavoriteTab(activeTab, setActiveTab, ['history', 'activity']);

  const [historyRows, setHistoryRows] = useState<HistoryReportRow[]>([]);
  const [activityRows, setActivityRows] = useState<ActivityReportRow[]>([]);
  const [activityOldest, setActivityOldest] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);
  const [seriesPoints, setSeriesPoints] = useState<SeriesPoint[]>([]);
  const [view, setView] = useState<ViewId | null>(null);
  const [bucketOverride, setBucketOverride] = useState<SeriesBucket | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    userHistoryReportService.listUsers(controller.signal)
      .then((res) => { setUsers(res.users); setGroups(res.groups); })
      .catch(() => { setUsers([]); setGroups([]); })
      .finally(() => setUsersLoading(false));
    return () => controller.abort();
  }, []);

  /** Display names of everyone the server resolved, so the Ranked view can show a
   *  person with NO activity at zero rather than omitting them. "Did nothing in this
   *  window" is the finding you are most likely to be looking for; dropping the row
   *  hides it. Taken from the RESPONSE, not the picker: a group is one option here and
   *  its members — including the `v1:` ones — are only known after expansion. */
  const selectedUserNames = useMemo(
    () => resolvedUsers.map((u) => u.display_name).filter(Boolean),
    [resolvedUsers]);

  /** Earliest selectable day. Computed at render, not as a module constant, so a tab
   *  left open across midnight does not keep offering yesterday's floor. */
  const lookbackFloor = useMemo(() => daysAgo(MAX_LOOKBACK_DAYS), []);

  /** Days in the selected window, used to pick the default view and bucket. */
  const rangeDays = useMemo(() => {
    if (!dateFrom || !dateTo) return DEFAULT_DAYS;
    return Math.max(0, Math.round(
      (dateTo.getTime() - dateFrom.getTime()) / 86400000));
  }, [dateFrom, dateTo]);

  /** `view` stays null until someone chooses, so the default can follow the range
   *  instead of freezing at whatever the page opened with. An explicit choice wins
   *  and keeps winning. */
  const effectiveView: ViewId =
    view ?? (rangeDays > CHART_DEFAULT_OVER_DAYS ? 'ranked' : 'table');
  const isSummary = effectiveView === 'ranked' || effectiveView === 'trend';
  // Ranking counts ACTIVE DAYS, which only means anything on day buckets — hourly
  // would count active hours and silently change what the metric says.
  const bucket: SeriesBucket = effectiveView === 'ranked'
    ? 'day'
    : (bucketOverride ?? (rangeDays <= HOURLY_MAX_DAYS ? 'hour' : 'day'));

  /** What a request is FOR, so switching views does not re-ask the same question.
   *
   *  Ranked and Trend both read /series, and over a range longer than a week they ask
   *  for the same day buckets — so flipping between them was issuing an identical
   *  query and paying for it twice. Table reads the detail endpoints, which do not
   *  care about the bucket at all. Keyed separately, because the two kinds of request
   *  are invalidated by different things. */
  const seriesKey = useMemo(() => JSON.stringify(
    [activeTab, selectedUserIds, ymd(dateFrom), ymd(dateTo), bucket]),
    [activeTab, selectedUserIds, dateFrom, dateTo, bucket]);
  const detailKey = useMemo(() => JSON.stringify(
    [activeTab, selectedUserIds, ymd(dateFrom), ymd(dateTo)]),
    [activeTab, selectedUserIds, dateFrom, dateTo]);

  // Marked only on SUCCESS, so a failed load retries instead of caching the error.
  const loadedSeries = useRef<string | null>(null);
  const loadedDetail = useRef<string | null>(null);

  const load = useCallback((signal: AbortSignal) => {
    // Nothing selected is not an error and must not fire a request — the server
    // would return an empty set, which reads identically to "these people did
    // nothing" once rendered.
    if (selectedUserIds.length === 0) {
      setHistoryRows([]);
      setActivityRows([]);
      setSeriesPoints([]);
      setRowCount(0);
      loadedSeries.current = null;
      loadedDetail.current = null;
      return Promise.resolve();
    }

    // Already have exactly this answer — switching view is a render, not a round trip.
    if (isSummary ? loadedSeries.current === seriesKey
                  : loadedDetail.current === detailKey) {
      return Promise.resolve();
    }
    setLoading(true);
    setError(null);
    const query = {
      userIds: selectedUserIds,
      startDate: ymd(dateFrom),
      endDate: ymd(dateTo),
    };

    // The chart asks the aggregate; only the table pulls detail rows. Fetching the
    // rows anyway "in case they switch" would reinstate the exact cost the chart
    // exists to avoid — a month for one busy person is ~316k rows.
    if (isSummary) {
      return userHistoryReportService
        .series({ ...query, source: activeTab, bucket }, signal)
        .then((res) => {
          setSeriesPoints(res.points);
          setResolvedUsers(res.users);
          loadedSeries.current = seriesKey;
        })
        .catch((e: any) => {
          if (signal.aborted) return;
          setError(e?.response?.data?.detail || e?.message || 'Failed to load');
        })
        .finally(() => { if (!signal.aborted) setLoading(false); });
    }

    const request = activeTab === 'history'
      ? userHistoryReportService.history(query, signal).then((res) => {
        setHistoryRows(res.items);
        setRowCount(res.row_count);
      })
      : userHistoryReportService.activity(query, signal).then((res) => {
        setActivityRows(res.items);
        setActivityOldest(res.activity_oldest);
        setRowCount(res.row_count);
      });
    return request
      .then(() => { loadedDetail.current = detailKey; })
      .catch((e: any) => {
        if (signal.aborted) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load');
      })
      .finally(() => { if (!signal.aborted) setLoading(false); });
  }, [selectedUserIds, dateFrom, dateTo, activeTab, isSummary, bucket, seriesKey, detailKey]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /** People first, then a clearly separated Groups section. `preserveOrder` is what
   *  actually keeps them apart — without it MultiSelect sorts alphabetically and the
   *  group labels interleave with the names. */
  const userOptions = useMemo<MultiSelectOption[]>(() => {
    // Departed staff are kept but marked: this is a report ABOUT past work, and the
    // people whose work most needs reviewing are often the ones who have left.
    const people = users.map((u) => ({
      value: u.user_id,
      label: u.active === false ? `${u.display_name} (inactive)` : u.display_name,
    }));
    if (groups.length === 0) return people;
    return [
      ...people,
      { value: '__groups_divider__', label: '', isDivider: true },
      { value: '__groups_header__', label: 'Groups', isHeader: true },
      // Selecting one of these sends its members, not the group — the value carries
      // the member tokens and the backend expands them.
      ...groups.map((g) => ({ value: g.value, label: `${g.label} (${g.count})` })),
    ];
  }, [users, groups]);

  /** The range asked for reaches further back than activity was kept for these
   *  people. Shown only when it is both true and relevant — never on the History
   *  tab, which has no retention limit. */
  const activityGap = useMemo(() => {
    if (activeTab !== 'activity' || !activityOldest || !dateFrom) return null;
    const oldest = new Date(activityOldest);
    if (isNaN(oldest.getTime()) || oldest <= dateFrom) return null;
    return oldest;
  }, [activeTab, activityOldest, dateFrom]);

  /** A PRO value as a link that stays highlighted once opened.
   *
   *  Wrapped in ProTableCell directly rather than via TanTable's
   *  `trackViewedColumnId`: that wraps a whole column, and on the Activity grid the
   *  Detail column is only a pronumber for `pro_view` rows — every other row holds
   *  search text, which would then highlight itself as a "viewed PRO". Wrapping here
   *  marks exactly the values actually rendered as PRO links, on both grids. */
  const proLinkCell = (value: string) => {
    const pro = Number(value);
    if (Number.isFinite(pro) && pro > 0) {
      return (
        <ProTableCell pro={pro}>
          <button
            onClick={() => setSelectedPro(pro)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
            title={`Open PRO ${pro}`}
          >
            {value}
          </button>
        </ProTableCell>
      );
    }
    return <span className="text-sm">{value}</span>;
  };

  const historyColumns = useMemo<ColumnDef<HistoryReportRow>[]>(() => [
    {
      accessorKey: 'datetime',
      header: 'Date/Time',
      cell: ({ getValue }) => (
        <span className="text-sm whitespace-nowrap">{formatDateTime(getValue<string>())}</span>
      ),
    },
    // Leads the record columns because the whole point of this report over the modal
    // is that rows come from several people. Resolved server-side to a display name:
    // the raw `userid` is whichever of the two identities wrote the row, so the same
    // person appears under two values and grouping on it splits them.
    { accessorKey: 'user_name', header: 'User', filterFn: 'arrIncludes' },
    { accessorKey: 'dbtable', header: 'Table', filterFn: 'arrIncludes' },
    {
      accessorKey: 'keyvalue',
      header: 'Key',
      // keyvalue is always a PRO number in cms.history — the numeric guard just
      // protects against blank/odd legacy rows.
      cell: ({ getValue }) => proLinkCell(getValue<string>()),
    },
    { accessorKey: 'colname', header: 'Column', filterFn: 'arrIncludes' },
    {
      accessorKey: 'oldval',
      header: 'Old Value',
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>() ?? ''} />,
    },
    {
      accessorKey: 'newval',
      header: 'New Value',
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>() ?? ''} />,
    },
    { accessorKey: 'type', header: 'Type', filterFn: 'arrIncludes' },
    {
      accessorKey: 'otherid',
      header: 'Other ID',
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>() ?? ''} />,
    },
    // Kept alongside the resolved name rather than replaced by it: which identity
    // stamped a row says which application wrote it, and that is a real finding when
    // tracking what moved to 360 and what is still being done in V1. Same header the
    // modal uses, so the two grids read the same.
    { accessorKey: 'userid', header: 'User ID', filterFn: 'arrIncludes' },
  ], []);

  const activityColumns = useMemo<ColumnDef<ActivityReportRow>[]>(() => [
    {
      accessorKey: 'occurred_at',
      header: 'Date/Time',
      cell: ({ getValue }) => (
        <span className="text-sm whitespace-nowrap">{formatDateTime(getValue<string>())}</span>
      ),
    },
    { accessorKey: 'user_name', header: 'User', filterFn: 'arrIncludes' },
    { accessorKey: 'event_type', header: 'Event', filterFn: 'arrIncludes' },
    { accessorKey: 'page_path', header: 'Page', filterFn: 'arrIncludes' },
    {
      accessorKey: 'target',
      header: 'Target',
      cell: ({ getValue }) => <TruncatedCell value={getValue<string>() ?? ''} />,
    },
    {
      accessorKey: 'detail',
      header: 'Detail',
      // pro_view details are pronumbers — link them like the history tab.
      cell: ({ row, getValue }) => {
        const value = getValue<string>() ?? '';
        if (row.original.event_type === 'pro_view') return proLinkCell(value);
        return <TruncatedCell value={value} />;
      },
    },
    {
      accessorKey: 'duration_ms',
      header: 'Duration',
      cell: ({ getValue }) => (
        <span className="text-sm">{formatDuration(getValue<number | null>())}</span>
      ),
    },
  ], []);

  const tabButton = (id: TabId, label: string) => (
    <button
      key={id}
      type="button"
      role="tab"
      aria-selected={activeTab === id}
      onClick={() => setActiveTab(id)}
      className={`px-3 py-1.5 text-sm rounded-t border-b-2 -mb-px ${
        activeTab === id
          ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );

  /** Dates ride on the EXPORT name, not the card title. The grid header is read a
   *  hundred times a session and the range is already sitting in the filter bar above
   *  it; the spreadsheet leaves the app and has nothing else to say which window it
   *  covers, so the file name carries it. */
  const rangeLabel = `${ymd(dateFrom) ?? 'any'} to ${ymd(dateTo) ?? 'any'}`;
  const nothingSelected = selectedUserIds.length === 0;

  /** Back to how the page loads: nobody selected, trailing 30 days. A partial reset
   *  (clearing users but keeping a hand-edited range) is the state people forget they
   *  are in, so "clear" means all of it. */
  const clearFilters = () => {
    setSelectedUserIds([]);
    setDateFrom(daysAgo(DEFAULT_DAYS));
    setDateTo(startOfToday());
  };

  const isDefaultRange =
    ymd(dateFrom) === ymd(daysAgo(DEFAULT_DAYS)) && ymd(dateTo) === ymd(startOfToday());
  const hasFilters = selectedUserIds.length > 0 || !isDefaultRange;

  return (
    <div className="p-4 space-y-3 h-full flex flex-col">
      <div className="shrink-0">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">User History</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 max-w-3xl">
          What a set of people did over a date range: record changes they made, and the
          pages and PROs they worked in.
        </p>
      </div>

      <FilterBar wrap label="Filters">
        <MultiSelect
          options={userOptions}
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          placeholder="Select users…"
          loading={usersLoading}
          enableSearch
          searchPlaceholder="Search users…"
          size="sm"
          className="min-w-[16rem]"
          usePortal
          preserveOrder
        />
        <FilterBar.Divider />
        <div className="flex items-center gap-2">
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={dateFrom}
            onChange={(val) => setDateFrom((val as Date | null) ?? null)}
            minDate={lookbackFloor}
            maxDate={dateTo ?? undefined}
            // Commit on click: this is a filter, not a form. The Apply step bought
            // nothing here and cost a click on every date change.
            autoApply
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="Start"
          />
          <span className="text-xs text-gray-400">to</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={dateTo}
            onChange={(val) => setDateTo((val as Date | null) ?? null)}
            minDate={dateFrom ?? lookbackFloor}
            autoApply
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="End"
          />
        </div>
        {hasFilters && (
          <>
            <FilterBar.Divider />
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Clear filters
            </button>
          </>
        )}
      </FilterBar>

      <div className="shrink-0 flex items-center gap-1 border-b border-gray-200 dark:border-gray-700" role="tablist">
        {tabButton('history', 'Change History')}
        {tabButton('activity', 'Activity')}

        <div className="ml-auto flex items-center gap-2 pb-1">
          {effectiveView === 'trend' && (
            <select
              aria-label="Bucket"
              value={bucket}
              onChange={(e) => setBucketOverride(e.target.value as SeriesBucket)}
              className="text-xs rounded border border-gray-300 dark:border-gray-600 bg-panel text-body px-1.5 py-1"
            >
              <option value="hour">By hour</option>
              <option value="day">By day</option>
            </select>
          )}
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden">
            {(['ranked', 'trend', 'table'] as ViewId[]).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={effectiveView === v}
                onClick={() => setView(v)}
                className={`px-2 py-1 text-xs ${
                  effectiveView === v
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {v === 'ranked' ? 'Ranked' : v === 'trend' ? 'Trend' : 'Table'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="shrink-0 text-sm text-red-600 dark:text-red-400">{error}</div>
      )}

      {/* Retention, stated where it would otherwise be mistaken for an idle employee. */}
      {activityGap && (
        <div className="shrink-0 text-xs text-amber-700 dark:text-amber-400">
          Activity is only kept back to {formatDateTime(activityGap.toISOString()).slice(0, 10)}
          {' '}for the selected users. Earlier dates in this range have been pruned, and
          show as no activity rather than none having happened. Change History is unaffected.
        </div>
      )}

      {/* A statement about COST, never about completeness. Nothing is withheld: the
          server returns every matching row. This only warns that a result this size
          will be slow to scroll and slow to export, so a narrower window is often the
          faster way to the same answer. */}
      {effectiveView === 'table' && rowCount >= LARGE_RESULT_ROWS && (
        <div className="shrink-0 text-xs text-amber-700 dark:text-amber-400">
          {num(rowCount)} rows. All of them are here, but a result this large is slow to
          scroll and to export. Narrowing the dates or the user list will be quicker if
          you are looking for something specific.
        </div>
      )}

      <div className="flex-1 min-h-0">
        {nothingSelected ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            Select one or more users to run the report.
          </div>
        ) : effectiveView === 'ranked' ? (
          <RankedUsersChart
            points={seriesPoints}
            allUserNames={selectedUserNames}
            loading={loading}
          />
        ) : effectiveView === 'trend' ? (
          <HitsTrendChart
            points={seriesPoints}
            bucket={bucket}
            startDate={ymd(dateFrom)}
            endDate={ymd(dateTo)}
            loading={loading}
          />
        ) : activeTab === 'history' ? (
          <TanTable
            // Distinct key per tab: the two grids share this ternary slot with different
            // settingsKeys, so without a key React reuses ONE TanTable instance across the
            // switch and only swaps settingsKey. TanTable loads settings once per mount,
            // so its debounced autosave would then write the stale layout into the other
            // tab's key and wipe it. (Same reasoning as UserHistoryModal.)
            key="user-history-report"
            data={historyRows}
            columns={historyColumns}
            loading={loading}
            settingsKey="user-history-report"
            seedLayoutFromCache
            cardTitle="Change History"
            exportName={`Change History ${rangeLabel}`}
            searchPlaceholder="Search change history..."
            enableColumnFilters
            columnFilterTypes={{
              datetime: 'text', keyvalue: 'text', oldval: 'text',
              newval: 'text', otherid: 'text',
            }}
            enableSearch
            enableExcelExport
            // One save writes one row PER FIELD, so a four-field edit is four rows at
            // the same timestamp. Grouping lets the reader collapse that back to the
            // save — by Key for "everything done to this PRO", or by Date/Time for
            // "everything done in this one action".
            enableGrouping
            defaultSorting={[{ id: 'datetime', desc: true }]}
            fillContainer
          />
        ) : (
          <TanTable
            // See the Record Changes grid above — distinct key keeps this a separate
            // instance so the two tabs never cross-write each other's layout.
            key="user-activity-report"
            data={activityRows}
            columns={activityColumns}
            loading={loading}
            settingsKey="user-activity-report"
            seedLayoutFromCache
            cardTitle="Activity"
            exportName={`Activity ${rangeLabel}`}
            searchPlaceholder="Search activity..."
            enableColumnFilters
            columnFilterTypes={{ occurred_at: 'text', target: 'text', detail: 'text' }}
            enableSearch
            enableExcelExport
            defaultSorting={[{ id: 'occurred_at', desc: true }]}
            fillContainer
          />
        )}
      </div>

      {selectedPro != null && (
        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen
          onClose={() => setSelectedPro(null)}
        />
      )}
    </div>
  );
}

export default function UserHistoryReport() {
  return (
    <ViewedProsProvider>
      <UserHistoryReportContent />
    </ViewedProsProvider>
  );
}
